import _ from 'lodash';
import moment from 'moment-timezone';
import { TZ } from '../../../../shared/consts';
import {
  INPUT_FMT, STATS, GRANULARITY_TO_DURATION,
} from '../../common/constants';

const wiresToTotal = (wires, key) => (wires || []).reduce(
  (result, wire) => result + _.get(wire, key, []).reduce((n, stat) => n + _.get(stat, 'c', 0), 0),
  0,
);

const earliestStatsUnit = ({ wires, granularity = 'd' } = {}) => {
  let earliestDate = null;
  let earliestPubDate = moment.tz(TZ);

  (wires || []).forEach(wire => {
    // publishedDate is in ISO96001 format, which already contains timezone
    // offset. Do NOT use `moment.tz` on this string.
    const pubDate = moment(wire.publishedDate);
    if (pubDate.isBefore(earliestPubDate)) {
      earliestPubDate = pubDate;
    }
    STATS.forEach(key => {
      _.get(wire, key, []).forEach(item => {
        const date = _.get(item, 'd');
        const count = _.get(item, 'c');
        if (date && (typeof count === 'number')) {
          const statsDate = moment.tz(date, INPUT_FMT, TZ);
          // always use negative isSameOrAfter to cope with the initial null
          // value
          if (!statsDate.isSameOrAfter(earliestDate)) {
            earliestDate = statsDate;
          }
        }
      });
    });
  });

  const result = moment().tz(TZ).diff(
    earliestDate ? moment(earliestDate, INPUT_FMT, TZ) : earliestPubDate,
    GRANULARITY_TO_DURATION[granularity] || 'days',
  );

  return result;
};

const wiresToTimeSeries = wires => {
  const timeseries = {};

  (wires || []).forEach(wire => {
    STATS.forEach(key => {
      _.get(wire, key, []).forEach(item => {
        const date = _.get(item, 'd');
        const count = _.get(item, 'c');
        if (date && (typeof count === 'number')) {
          timeseries[date] = timeseries[date] || {};
          timeseries[date][key] = timeseries[date][key] || 0;
          timeseries[date][key] += count;
        }
      });
    });
  });

  return timeseries;
};

const timeseriesToBucket = ({
  ts, key, start, end, granularity,
}) => {
  const result = [];
  const duration = GRANULARITY_TO_DURATION[granularity] || 'days';

  // With start and end being days away from today, we're using a range like:
  //   (start, end]
  // That means start of day of 1 day after start date to end of day end date.
  let pos = start;
  const t = moment()
    .tz(TZ)
    .add(1, duration)
    .startOf(duration)
    .subtract(pos, duration);
  while (pos > end) {
    const startDay = t.format(INPUT_FMT);
    const singleBucket = { time: startDay, value: 0 };
    if (granularity === 'm') {
      singleBucket.value = _.times(31).reduce((v, d) => {
        const thisDay = t.clone().add(d, 'days');
        return (thisDay.month() === t.month())
          ? v + _.get(ts, `${thisDay.format(INPUT_FMT)}.${key}`, 0)
          : v;
      }, 0);
    } else if (granularity === 'w') {
      singleBucket.value = _.times(7).reduce((v, d) => {
        const thisDay = t.clone().add(d, 'days').format(INPUT_FMT);
        return v + _.get(ts, `${thisDay}.${key}`, 0);
      }, 0);
    } else { // 'd' or any invalid values' fallback
      singleBucket.value = _.get(ts, `${startDay}.${key}`, 0);
    }
    result.push(singleBucket);
    pos -= 1;
    t.add(1, duration);
  }

  return result;
};

const wiresToBucket = ({
  wires, key, start, end, granularity,
}) => {
  const ts = wiresToTimeSeries(wires);
  return timeseriesToBucket({
    ts, key, start, end, granularity,
  });
};

export {
  earliestStatsUnit,
  wiresToBucket,
  wiresToTotal,
};
